import { BaseCommand } from './ICommand';
import { FrameManager } from '../frames/FrameManager';
import { Frame } from '../frames/IFrame';
import { useDiagramStore } from '../../stores/diagramStore';
import { Node, Edge } from '../models/scene';
import { getNodeMutationManager } from './mutations/NodeMutationManager';

export class DeleteFrameCascadeCommand extends BaseCommand {
  private readonly frameManager: FrameManager;
  private readonly targetFrameId: string;

  // Snapshots for undo
  private framesSnapshot: any[] = [];
  private deletedNodes: Node[] = [];
  private deletedEdges: Edge[] = [];
  private parentFrameId?: string;

  constructor(frameManager: FrameManager, frameId: string) {
    super('Delete Frame', `Delete frame and its contents`);
    this.frameManager = frameManager;
    this.targetFrameId = frameId;
  }

  private collectCascadeIds(rootId: string): { frameIds: string[]; nodeIds: string[]; edgeIds: string[] } {
    const frameIds: string[] = [];
    const nodeIdSet = new Set<string>();
    const edgeIdSet = new Set<string>();

    const visit = (fid: string) => {
      if (frameIds.includes(fid)) return;
      const f = this.frameManager.getFrame(fid);
      if (!f) return;
      frameIds.push(fid);
      // collect shapes/connectors in this frame
      f.shapes.forEach(id => nodeIdSet.add(id));
      f.connectors.forEach(id => edgeIdSet.add(id));
      // recurse children frames
      if (f.childFrames) {
        Array.from(f.childFrames).forEach(visit);
      }
    };

    visit(rootId);

    return {
      frameIds,
      nodeIds: Array.from(nodeIdSet),
      edgeIds: Array.from(edgeIdSet)
    };
  }

  execute(): void {
    if (!this.canExecute()) return;
    const store = useDiagramStore.getState();

    // Collect cascade ids and snapshots
    const { frameIds, nodeIds, edgeIds } = this.collectCascadeIds(this.targetFrameId);
    this.parentFrameId = this.frameManager.getFrame(this.targetFrameId)?.parentFrame;

    // Snapshot frames (deep) for undo
    this.framesSnapshot = frameIds
      .map(id => this.frameManager.getFrame(id))
      .filter(Boolean)
      .map(f => structuredClone((f as Frame).toJSON()));

    // Snapshot nodes/edges for undo
    this.deletedNodes = store.nodes.filter(n => nodeIds.includes(n.id));
    // Include any edges connected to nodes (in case frame.connectors missed some)
    const connectedEdgeSet = new Set<string>(edgeIds);
    store.edges.forEach(e => {
      if (nodeIds.includes(e.from) || nodeIds.includes(e.to)) {
        connectedEdgeSet.add(e.id);
      }
    });
    this.deletedEdges = store.edges.filter(e => connectedEdgeSet.has(e.id));

    // Perform deletions: edges first, then nodes, then frames (deepest first)
    const mutations = getNodeMutationManager();
    const doc = mutations.getDoc();
    const remove = () => {
      mutations.batchDeleteEdges(this.deletedEdges.map(e => e.id));
      mutations.batchDeleteNodes(this.deletedNodes.map(n => n.id));

      // Persistence belongs to the command, even while canvas listeners are
      // unmounted. Keep frame records atomic with their contents.
      for (let i = frameIds.length - 1; i >= 0; i--) {
        this.frameManager.removeFrame(frameIds[i]);
        doc?.getMap('frames').delete(frameIds[i]);
      }
      this.persistParent();
    };
    if (doc) doc.transact(remove, 'local'); else remove();
  }

  private persistParent(): void {
    const parent = this.parentFrameId ? this.frameManager.getFrame(this.parentFrameId) : undefined;
    if (parent) getNodeMutationManager().getDoc()?.getMap('frames').set(parent.id, parent.toJSON());
  }

  canExecute(): boolean {
    const store = useDiagramStore.getState();
    if (!store.canWrite || !this.frameManager.getFrame(this.targetFrameId)) return false;
    const { frameIds, nodeIds } = this.collectCascadeIds(this.targetFrameId);
    return !frameIds.some(id => this.frameManager.getFrame(id)?.locked)
      && !store.nodes.some(node => nodeIds.includes(node.id)
        && (node.locked || node.attrs?.locked === true));
  }

  undo(): void {
    if (!useDiagramStore.getState().canWrite) return;
    const mutations = getNodeMutationManager();
    const doc = mutations.getDoc();
    const restore = () => {
      // Restore parents before children, then node and connector records.
      this.framesSnapshot.forEach(json => {
        const frame = Frame.fromJSON(structuredClone(json));
        this.frameManager.addFrame(frame);
        doc?.getMap('frames').set(frame.id, frame.toJSON());
      });
      this.persistParent();
      this.deletedNodes.forEach(n => mutations.restoreNode({ nodeId: n.id, oldState: n, newState: {} }));
      this.deletedEdges.forEach(e => mutations.addEdge(e));
    };
    if (doc) doc.transact(restore, 'local'); else restore();
  }
}
